import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import type { GeneratedGraphData } from "./GeneratedGraphView";
import shoppingItemIcon from "../assets/img/product_icon.svg";
import recommendedItemIcon from "../assets/img/product_icon_blue.svg";
import sdmItemIcon from "../assets/img/product_icon_green.svg";
import userIcon from "../assets/img/user-icon.png";

interface SvgViewBox {
    minX: number;
    minY: number;
    width: number;
    height: number;
}

interface RenderableNode {
    id: number;
    svgX: number;
    svgY: number;
    hasProducts: boolean;
    isSpecial: boolean;
}

interface RenderableEdge {
    id: string;
    source: RenderableNode;
    target: RenderableNode;
}

interface StoreMapGraphOverlayProps {
    graphData: GeneratedGraphData;
    svgUrl: string;
    height?: string | number;
    routeNodeIds?: number[];
    showFullGraph?: boolean;
    shoppingListNodeIds?: number[];
    recommendedNodeIds?: number[];
    routeRecommendedNodeIds?: number[];
    userMarkerNodeId?: number | null;
    userMarkerGraphPoint?: { x: number; y: number } | null;
    onNodeClick?: (nodeId: number) => void;
}

const ENTRANCE_NODE_ID = 3517;
const CHECKOUT_NODE_ID = 360;
const SPECIAL_NODE_IDS = new Set([ENTRANCE_NODE_ID, CHECKOUT_NODE_ID]);
// Approximate drawable store area from the red outer border path in `planta.svg`.
// This maps the graph into the actual store footprint instead of the full SVG canvas.
const STORE_INNER_BOUNDS = {
    minX: 22.5,
    minY: 21,
    maxX: 1892,
    maxY: 729,
};
// Final manual tuning after fitting into the store border.
// These are intentionally small so we can fine-tune the overlay visually.
const CALIBRATION = {
    scale: 0.985,
    offsetX: -6,
    offsetY: 6,
};
const PRODUCT_ICON_WIDTH = 32;
const PRODUCT_ICON_HEIGHT = 32;
/** Blue (persona) recommended pins use a larger size so they stay visible. */
const RECOMMENDED_ICON_WIDTH = 32;
const RECOMMENDED_ICON_HEIGHT = 32;
const USER_ICON_WIDTH = 32;
const USER_ICON_HEIGHT = 32;

function getOverlayMarkerIcon(
    nodeId: number,
    shoppingSet: Set<number>,
    routeRecommendedSet: Set<number>,
    recommendedSet: Set<number>
) {
    if (shoppingSet.has(nodeId)) {
        return shoppingItemIcon;
    }
    if (routeRecommendedSet.has(nodeId)) {
        return sdmItemIcon;
    }
    if (recommendedSet.has(nodeId)) {
        return recommendedItemIcon;
    }
    return null;
}

function extractSvgViewBox(svgText: string): SvgViewBox {
    const viewBoxMatch = svgText.match(/viewBox\s*=\s*"([^"]+)"/i);
    if (viewBoxMatch) {
        const [minX, minY, width, height] = viewBoxMatch[1]
            .trim()
            .split(/[\s,]+/)
            .map(Number);

        if ([minX, minY, width, height].every((n) => Number.isFinite(n))) {
            return { minX, minY, width, height };
        }
    }

    const widthMatch = svgText.match(/width\s*=\s*"([\d.]+)"/i);
    const heightMatch = svgText.match(/height\s*=\s*"([\d.]+)"/i);

    return {
        minX: 0,
        minY: 0,
        width: Number(widthMatch?.[1] || 1911),
        height: Number(heightMatch?.[1] || 750),
    };
}

function prepareForegroundSvg(svgText: string): string {
    let next = svgText.replace(/fill="white"/gi, 'fill="transparent"');

    next = next.replace(
        /<svg\b/i,
        '<svg preserveAspectRatio="xMidYMid meet" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" style="display:block;width:100%;height:100%;pointer-events:none;"'
    );

    return next;
}

function computeGraphBounds(nodes: GeneratedGraphData["nodes"]) {
    const entries = Object.values(nodes);
    if (!entries.length) {
        return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
    }

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (const node of entries) {
        const [x = 0, y = 0] = node.coords || [];
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
    }

    return { minX, minY, maxX, maxY };
}

function mapGraphPointToSvg(
    point: { x: number; y: number },
    graphBounds: ReturnType<typeof computeGraphBounds>,
    svgViewBox: SvgViewBox
) {
    const graphWidth = Math.max(1, graphBounds.maxX - graphBounds.minX);
    const graphHeight = Math.max(1, graphBounds.maxY - graphBounds.minY);

    const normalizedX = (point.x - graphBounds.minX) / graphWidth;
    const normalizedY = (graphBounds.maxY - point.y) / graphHeight;

    const targetMinX = Math.max(svgViewBox.minX, STORE_INNER_BOUNDS.minX);
    const targetMinY = Math.max(svgViewBox.minY, STORE_INNER_BOUNDS.minY);
    const targetMaxX = Math.min(
        svgViewBox.minX + svgViewBox.width,
        STORE_INNER_BOUNDS.maxX
    );
    const targetMaxY = Math.min(
        svgViewBox.minY + svgViewBox.height,
        STORE_INNER_BOUNDS.maxY
    );
    const targetWidth = Math.max(1, targetMaxX - targetMinX);
    const targetHeight = Math.max(1, targetMaxY - targetMinY);
    const uniformScale = Math.min(
        targetWidth / graphWidth,
        targetHeight / graphHeight
    );
    const fittedWidth = graphWidth * uniformScale * CALIBRATION.scale;
    const fittedHeight = graphHeight * uniformScale * CALIBRATION.scale;
    const offsetX =
        targetMinX + (targetWidth - fittedWidth) / 2 + CALIBRATION.offsetX;
    const offsetY =
        targetMinY + (targetHeight - fittedHeight) / 2 + CALIBRATION.offsetY;

    return {
        x: offsetX + normalizedX * fittedWidth,
        y: offsetY + normalizedY * fittedHeight,
    };
}

export default function StoreMapGraphOverlay({
    graphData,
    svgUrl,
    height = "100%",
    routeNodeIds,
    showFullGraph = true,
    shoppingListNodeIds = [],
    recommendedNodeIds = [],
    routeRecommendedNodeIds = [],
    userMarkerNodeId = null,
    userMarkerGraphPoint = null,
    onNodeClick,
}: StoreMapGraphOverlayProps) {
    const [svgText, setSvgText] = useState("");
    const [svgViewBox, setSvgViewBox] = useState<SvgViewBox>({
        minX: 0,
        minY: 0,
        width: 1911,
        height: 750,
    });
    const [isLoadingSvg, setIsLoadingSvg] = useState(true);
    const [svgError, setSvgError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        async function loadSvg() {
            setIsLoadingSvg(true);
            setSvgError(null);

            try {
                const response = await fetch(svgUrl);
                if (!response.ok) {
                    setSvgError(`Failed to load SVG: ${response.status}`);
                    return;
                }

                const text = await response.text();
                if (cancelled) return;

                setSvgText(prepareForegroundSvg(text));
                setSvgViewBox(extractSvgViewBox(text));
            } catch (error) {
                if (cancelled) return;
                setSvgError(
                    error instanceof Error ? error.message : "Could not load store map."
                );
            } finally {
                if (!cancelled) setIsLoadingSvg(false);
            }
        }

        void loadSvg();
        return () => {
            cancelled = true;
        };
    }, [svgUrl]);

    const graphBounds = useMemo(
        () => computeGraphBounds(graphData.nodes),
        [graphData.nodes]
    );

    const renderableNodes = useMemo<RenderableNode[]>(() => {
        return Object.values(graphData.nodes).map((node) => {
            const [x = 0, y = 0] = node.coords || [];
            const svgPoint = mapGraphPointToSvg({ x, y }, graphBounds, svgViewBox);

            return {
                id: Number(node.node_id),
                svgX: svgPoint.x,
                svgY: svgPoint.y,
                hasProducts: Array.isArray(node.products) && node.products.length > 0,
                isSpecial: SPECIAL_NODE_IDS.has(Number(node.node_id)),
            };
        });
    }, [graphBounds, graphData.nodes, svgViewBox]);

    const nodeMap = useMemo(() => {
        const map = new Map<number, RenderableNode>();
        renderableNodes.forEach((node) => map.set(node.id, node));
        return map;
    }, [renderableNodes]);

    const renderableEdges = useMemo<RenderableEdge[]>(() => {
        return graphData.edges
            .map((edge, index) => {
                const source = nodeMap.get(Number(edge.source));
                const target = nodeMap.get(Number(edge.target));

                if (!source || !target) {
                    return null;
                }

                return {
                    id: `edge-${edge.source}-${edge.target}-${index}`,
                    source,
                    target,
                };
            })
            .filter((edge): edge is RenderableEdge => edge !== null);
    }, [graphData.edges, nodeMap]);

    const routePolyline = useMemo(() => {
        if (!routeNodeIds?.length) {
            return "";
        }

        return routeNodeIds
            .map((nodeId) => nodeMap.get(Number(nodeId)))
            .filter((node): node is RenderableNode => Boolean(node))
            .map((node) => `${node.svgX},${node.svgY}`)
            .join(" ");
    }, [nodeMap, routeNodeIds]);

    const firstSegmentPolyline = useMemo(() => {
        if (!routeNodeIds || routeNodeIds.length < 2 || !shoppingListNodeIds?.length) {
            return "";
        }

        const shoppingSet = new Set(shoppingListNodeIds.map((id) => Number(id)));

        // Find the index of the first route node that has shopping-list products.
        const targetIndex = routeNodeIds.findIndex((nodeId, index) => {
            if (index === 0) return false; // skip the very first node as "origin"
            return shoppingSet.has(Number(nodeId));
        });

        if (targetIndex <= 0) {
            return "";
        }

        const segmentIds = routeNodeIds.slice(0, targetIndex + 1);

        const nodes = segmentIds
            .map((nodeId) => nodeMap.get(Number(nodeId)))
            .filter((node): node is RenderableNode => Boolean(node));

        if (nodes.length < 2) {
            return "";
        }

        return nodes.map((node) => `${node.svgX},${node.svgY}`).join(" ");
    }, [nodeMap, routeNodeIds, shoppingListNodeIds]);
    const routeArrows = useMemo(() => {
        if (!routeNodeIds || routeNodeIds.length < 2) {
            return [];
        }

        return routeNodeIds
            .map((nodeId) => nodeMap.get(Number(nodeId)))
            .filter((node): node is RenderableNode => Boolean(node))
            .slice(1)
            .map((target, index) => {
                const source = routeNodeIds
                    .map((nodeId) => nodeMap.get(Number(nodeId)))
                    .filter((node): node is RenderableNode => Boolean(node))[index];
                if (!source) {
                    return null;
                }

                const dx = target.svgX - source.svgX;
                const dy = target.svgY - source.svgY;
                const length = Math.hypot(dx, dy);

                if (length < 18) {
                    return null;
                }

                return {
                    key: `route-arrow-${source.id}-${target.id}-${index}`,
                    x: source.svgX + dx * 0.5,
                    y: source.svgY + dy * 0.5,
                    angle: (Math.atan2(dy, dx) * 180) / Math.PI,
                };
            })
            .filter(
                (
                    arrow
                ): arrow is {
                    key: string;
                    x: number;
                    y: number;
                    angle: number;
                } => Boolean(arrow)
            );
    }, [nodeMap, routeNodeIds]);

    const shoppingSet = useMemo(
        () => new Set(shoppingListNodeIds.map((id) => Number(id))),
        [shoppingListNodeIds]
    );
    const recommendedSet = useMemo(
        () => new Set(recommendedNodeIds.map((id) => Number(id))),
        [recommendedNodeIds]
    );
    const routeRecommendedSet = useMemo(
        () => new Set(routeRecommendedNodeIds.map((id) => Number(id))),
        [routeRecommendedNodeIds]
    );
    const userMarkerNode = useMemo(
        () => (userMarkerNodeId != null ? nodeMap.get(Number(userMarkerNodeId)) ?? null : null),
        [nodeMap, userMarkerNodeId]
    );
    const userMarkerSvgPoint = useMemo(() => {
        if (!userMarkerGraphPoint) return null;
        return mapGraphPointToSvg(userMarkerGraphPoint, graphBounds, svgViewBox);
    }, [graphBounds, svgViewBox, userMarkerGraphPoint]);

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                height,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                overflow: "hidden",
                backgroundColor: "#fafafa",
            }}
        >
            {isLoadingSvg && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 4,
                        backgroundColor: "rgba(255,255,255,0.7)",
                    }}
                >
                    <CircularProgress size={28} />
                </Box>
            )}

            {svgError && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 4,
                        p: 2,
                    }}
                >
                    <Typography color="error">{svgError}</Typography>
                </Box>
            )}

            <svg
                viewBox={`${svgViewBox.minX} ${svgViewBox.minY} ${svgViewBox.width} ${svgViewBox.height}`}
                preserveAspectRatio="xMidYMid meet"
                shapeRendering="geometricPrecision"
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 1,
                }}
                aria-hidden="true"
            >
                {showFullGraph &&
                    renderableEdges.map((edge) => (
                        <line
                            key={edge.id}
                            x1={edge.source.svgX}
                            y1={edge.source.svgY}
                            x2={edge.target.svgX}
                            y2={edge.target.svgY}
                            stroke="transparent"
                            strokeWidth={1.1}
                            opacity={0}
                        />
                    ))}

                {routePolyline && (
                    <>
                        {/* Full route - very light background route */}
                        <polyline
                            points={routePolyline}
                            fill="none"
                            stroke="#fca5a5"
                            strokeWidth={3}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            opacity={0.25}
                        />
                        <polyline
                            points={routePolyline}
                            fill="none"
                            stroke="#dc2626"
                            strokeWidth={3}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            opacity={0.25}
                        />

                        {/* First segment - strong highlight */}
                        {firstSegmentPolyline && (
                            <>
                                <polyline
                                    points={firstSegmentPolyline}
                                    fill="none"
                                    stroke="#dc2626"
                                    strokeWidth={6}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    opacity={1}
                                />
                            </>
                        )}

                        {routeArrows.map((arrow) => (
                            <g
                                key={arrow.key}
                                transform={`translate(${arrow.x} ${arrow.y}) rotate(${arrow.angle})`}
                                opacity={0.95}
                                pointerEvents="none"
                            >
                                <path d="M -7 -5 L 7 0 L -7 5 L -2 0 Z" fill="#dc2626" />
                            </g>
                        ))}
                    </>
                )}

                {showFullGraph &&
                    renderableNodes
                        .filter((node) => !node.isSpecial && !node.hasProducts)
                        .map((node) => (
                            <circle
                                key={node.id}
                                cx={node.svgX}
                                cy={node.svgY}
                                r={2.2}
                                fill="transparent"
                                opacity={0}
                            />
                        ))}

                {showFullGraph &&
                    renderableNodes
                        .filter((node) => !node.isSpecial && node.hasProducts)
                        .map((node) => (
                            <circle
                                key={node.id}
                                cx={node.svgX}
                                cy={node.svgY}
                                r={3}
                                fill="transparent"
                                opacity={0}
                            />
                        ))}

                {renderableNodes
                    .filter((node) => node.isSpecial)
                    .map((node) => (
                        <circle
                            key={node.id}
                            cx={node.svgX}
                            cy={node.svgY}
                            r={7}
                            fill="#4caf50"
                            opacity={0.95}
                        />
                    ))}

            </svg>

            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                }}
                dangerouslySetInnerHTML={{ __html: svgText }}
            />

            <svg
                viewBox={`${svgViewBox.minX} ${svgViewBox.minY} ${svgViewBox.width} ${svgViewBox.height}`}
                preserveAspectRatio="xMidYMid meet"
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 3,
                    pointerEvents: "none",
                }}
                aria-hidden="true"
            >
                {renderableNodes
                    .map((node) => ({
                        node,
                        icon: getOverlayMarkerIcon(
                            node.id,
                            shoppingSet,
                            routeRecommendedSet,
                            recommendedSet
                        ),
                    }))
                    .filter(
                        (
                            entry
                        ): entry is {
                            node: RenderableNode;
                            icon: string;
                        } => Boolean(entry.icon)
                    )
                    .map(({ node, icon }) => {
                        const isBlueRecommended =
                            recommendedSet.has(node.id) &&
                            !shoppingSet.has(node.id) &&
                            !routeRecommendedSet.has(node.id);
                        const w = isBlueRecommended ? RECOMMENDED_ICON_WIDTH : PRODUCT_ICON_WIDTH;
                        const h = isBlueRecommended ? RECOMMENDED_ICON_HEIGHT : PRODUCT_ICON_HEIGHT;
                        return (
                            <image
                                key={`foreground-marker-${node.id}`}
                                href={icon}
                                x={node.svgX - w / 2}
                                y={node.svgY - h}
                                width={w}
                                height={h}
                                opacity={0.95}
                                pointerEvents="none"
                            />
                        );
                    })}

                {(userMarkerSvgPoint || userMarkerNode) && (
                    <image
                        href={userIcon}
                        x={(userMarkerSvgPoint?.x ?? userMarkerNode!.svgX) - USER_ICON_WIDTH / 2}
                        y={(userMarkerSvgPoint?.y ?? userMarkerNode!.svgY) - USER_ICON_HEIGHT}
                        width={USER_ICON_WIDTH}
                        height={USER_ICON_HEIGHT}
                        opacity={0.98}
                        pointerEvents="none"
                    />
                )}
            </svg>


            {onNodeClick && (
                <svg
                    viewBox={`${svgViewBox.minX} ${svgViewBox.minY} ${svgViewBox.width} ${svgViewBox.height}`}
                    preserveAspectRatio="xMidYMid meet"
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        zIndex: 4,
                        background: "transparent",
                    }}
                    aria-hidden="true"
                >
                    {renderableNodes.map((node) => {
                        const hasMarker = Boolean(
                            getOverlayMarkerIcon(
                                node.id,
                                shoppingSet,
                                routeRecommendedSet,
                                recommendedSet
                            )
                        );

                        if (!hasMarker) {
                            return null;
                        }

                        const isBlueRecommended =
                            recommendedSet.has(node.id) &&
                            !shoppingSet.has(node.id) &&
                            !routeRecommendedSet.has(node.id);
                        const w = isBlueRecommended ? RECOMMENDED_ICON_WIDTH : PRODUCT_ICON_WIDTH;
                        const h = isBlueRecommended ? RECOMMENDED_ICON_HEIGHT : PRODUCT_ICON_HEIGHT;
                        const pad = 10;
                        return (
                            <rect
                                key={`hit-marker-${node.id}`}
                                x={node.svgX - w / 2 - pad}
                                y={node.svgY - h - pad}
                                width={w + pad * 2}
                                height={h + pad * 2}
                                rx={10}
                                ry={10}
                                fill="rgba(0,0,0,0.001)"
                                pointerEvents="all"
                                style={{ cursor: "pointer" }}
                                onClick={() => onNodeClick(node.id)}
                            />
                        );
                    })}
                </svg>
            )}
        </Box>
    );
}
